import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { createFilterOptions } from "@mui/material/Autocomplete";

import Styles from "./comboBox.module.scss";

const filter = createFilterOptions();

// A picker that also accepts values that are not in the list yet.
//
// Roles are stored as free text and group players in the auction, so the
// spelling has to match exactly across every player. A plain text box made
// that easy to get wrong: "All Rounder" and "All rounder" silently became two
// groups. This offers the roles already in use, while still allowing a genuinely
// new one to be typed and added.
export default function ComboBox({
  value,
  onChange,
  options = [],
  placeholder = "Select or type",
  name,
  disabled,
}) {
  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      disabled={disabled}
      className={Styles.combo}
      // The menu renders in a portal, so it cannot inherit the field's styling
      // and has to be dressed through these props instead.
      componentsProps={{ paper: { className: Styles.paper } }}
      ListboxProps={{ className: Styles.listbox }}
      value={value || ""}
      options={options}
      // Values arrive as a plain string when typed, or as the {inputValue,
      // label} shape below when the "Add ..." row is chosen.
      onChange={(event, next) => {
        if (next === null) return onChange("");
        if (typeof next === "string") return onChange(next);
        if (next?.inputValue) return onChange(next.inputValue);
        onChange(next?.label ?? "");
      }}
      onInputChange={(event, next, reason) => {
        if (reason === "input") onChange(next);
      }}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const typed = params.inputValue.trim();
        // Only offer to add when it is not already an exact match, so the
        // list does not show "Add Bowler" while Bowler sits right above it.
        const exists = opts.some(
          (o) => (typeof o === "string" ? o : o.label) === typed
        );
        if (typed !== "" && !exists) {
          filtered.push({ inputValue: typed, label: `Add "${typed}"` });
        }
        return filtered;
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        if (option?.inputValue) return option.inputValue;
        return option?.label ?? "";
      }}
      renderOption={(props, option) => {
        const isNew = typeof option !== "string" && option?.inputValue;
        return (
          <li {...props} className={`${props.className} ${Styles.option}`}>
            {isNew ? (
              <span className={Styles.addNew}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                {option.label}
              </span>
            ) : (
              <span>{typeof option === "string" ? option : option.label}</span>
            )}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          placeholder={placeholder}
          variant="outlined"
        />
      )}
    />
  );
}
